/**
 * The planning grid — design/09 §8 and §11 `09.U2`.
 *
 *   Exit criterion: "Grid renders 100k rows at 60fps with frozen headers and inline
 *   edit." Measured by `npm run bench:grid`, which drives a real browser and reports
 *   a real frame time; the number is recorded in docs/adr/0003.
 *
 * COMPOSITION, NOT A SWAP — design/09 §10.2.
 *
 * AG Grid Enterprise bundles exactly what a planning grid needs (pivot, row grouping
 * with aggregation, range selection and fill handle, master-detail, rich clipboard)
 * and charges per developer for it. §10.2 calls that "the trap" and records the
 * decision to compose instead:
 *
 *   grouping, aggregation, virtualisation  TanStack Table + TanStack Virtual (MIT)
 *   Excel-style editing at very large row  this component's own edit layer; Glide
 *     counts                               Data Grid only if DOM rendering hits a
 *                                          MEASURED performance wall
 *   pivot                                  bespoke over our own aggregation layer
 *
 * "This trades engineering weeks against a recurring per-developer licence and vendor
 * lock-in." Recorded in docs/adr/0003 with the cost stated, because §10.2 warns this
 * is "the line most likely to be re-litigated under delivery pressure".
 *
 * WHY VIRTUALISATION IS NOT OPTIONAL: G2.3 requires 100k rows at 60fps. 60fps is a
 * 16.7 ms frame budget. A DOM row of twelve cells costs roughly 0.05 ms to create, so
 * 100k rows is ~60 s of layout — three orders of magnitude over budget. Only the
 * visible window can exist, which is why the row model and the DOM are separate here
 * and why `rowCount` is a number rather than `rows.length`.
 */

import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  columnSizingFeature, createExpandedRowModel, createSortedRowModel, flexRender,
  rowExpandingFeature, rowSortingFeature, sortFn_alphanumeric, sortFn_basic, sortFn_text,
  tableFeatures, useTable, type ColumnDef, type ExpandedState, type SortingState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChevronRight, ChevronDown, Info } from 'lucide-react';
import { DENSITY_TOTAL_ROW_HEIGHT, densityRowHeight, type DensityMode } from '../../tokens/generated/tokens';
import { densityRowStyle } from '../density';
import { FOCUS_RING_INSET, FOCUS_RING_WITHIN } from '../../primitives/focus';
import { TruncatedText } from '../../primitives/truncated-text';
import { formatCurrency, formatNumber } from '../../lib/format';
import { isNumeric, toNumberOrNull, type CellEdit, type CellValue, type Lineage, type MeasureDefinition } from '../types';
import { RowCapNotice } from '../row-cap';
import { gridRequest } from './grid-export';
import type { GridSort } from '../templates/url';

// ── row contract ─────────────────────────────────────────────────────────────

export interface GridRow {
  readonly id: string;
  /** Hierarchy depth, 0-based. Drives the indent and the expander. */
  readonly depth: number;
  /** Reference data, not a catalogue key. */
  readonly label: string;
  readonly hasChildren: boolean;
  /** Keyed by measure key. */
  readonly values: Readonly<Record<string, CellValue>>;
  readonly lineage: Readonly<Record<string, Lineage>>;
  /** A subtotal or total row is set in `type-numeric-strong` (§4.2). */
  readonly isTotal?: boolean;
  /**
   * A GROUP'S HEADING ROW (brief part 3 §C3, ADR 9038): 36px on the constants ground, its label —
   * the group's name and count, already a sentence — across the whole row, no figures. Its
   * `children` follow it and are open. It is not a data row: the listing band's count and
   * position, the row count and the CSV skip it.
   */
  readonly group?: boolean;
  readonly children?: readonly GridRow[];
}

/** A group's heading row is this tall in every density — the approvals queue's group row (ADR 9032). */
export const GRID_GROUP_ROW_HEIGHT = 36;

/** The rows in view, counted over data rows only: a group's heading row is not one. Pure. */
export function listingPosition(
  range: { readonly startIndex: number; readonly endIndex: number } | null,
  rows: readonly { readonly group?: boolean }[],
): { readonly from: number; readonly to: number; readonly total: number } {
  const total = rows.filter((row) => row.group !== true).length;
  if (range === null || total === 0) return { ...gridPosition(null, total), total };
  const before = (index: number): number => rows.slice(0, index + 1).filter((row) => row.group !== true).length;
  const first = rows.findIndex((row, index) => index >= range.startIndex && row.group !== true);
  const from = first === -1 ? total : before(first);
  return { from: Math.min(total, Math.max(1, from)), to: Math.min(total, Math.max(from, before(range.endIndex))), total };
}

export interface PlanningGridProps {
  readonly rows: readonly GridRow[];
  readonly measures: readonly MeasureDefinition[];
  /** design/09 §6 — three row heights, user-selectable and PERSISTED per user. */
  readonly density: DensityMode;
  /**
   * Column keys pinned to the inline start. The row label column is always pinned; a
   * planner scrolling right must never lose the name of the thing they are editing.
   */
  readonly pinnedColumns?: readonly string[];
  readonly onEdit?: (edit: CellEdit) => void;
  readonly onLineageRequest?: (rowId: string, columnKey: string, lineage: Lineage) => void;
  /**
   * The row was ACTIVATED — Enter, or a double click — rather than edited or expanded.
   *
   * This is the drill affordance, and it exists because ch04 asked for it through the
   * channel CLAUDE.md §2.2 prescribes rather than working around it: the markdown batch
   * table needs to narrow `?optionKeys=` to one option by picking a row, found no callback,
   * and shipped a combobox beside the grid instead. See `docs/adr/0089`. Every chapter with
   * a `drill` target in its manifest has the same need, and a grid whose rows cannot be
   * activated pushes each of them into a second navigation control beside the first.
   *
   * Optional, and a grid without it is not degraded: a row is only activatable where the
   * screen has somewhere to send it. Where it IS supplied the row label becomes a real
   * `<button>` carrying `grid.activateRow` as its accessible name, so the affordance is
   * announced and keyboard-reachable rather than being a double-click nobody can discover.
   */
  readonly onRowActivate?: (rowId: string) => void;
  /** Accessible name. G6 — an ICU key. */
  readonly labelKey: string;
  /** Height of the scroll viewport in pixels. The grid uses the full viewport by
   *  default: §6 sets maximum content width to NONE, because "a centred 1200px column
   *  wastes half an ultrawide monitor, which is what planners actually use". */
  readonly viewportHeight: number;
  /** Overscan rows. Tuned in the benchmark, not guessed. */
  readonly overscan?: number;
  /**
   * The grid as one long sorted list, with its count and position always in view — the
   * designer's brief part 2 §7.1 (Trasferimenti: every row in the virtualised grid, no photo,
   * no filters), `docs/adr/9032`. When set:
   *
   *   * a band above the header reads «{N} trasferimenti · ordinati per {colonna} ↓ · righe X–Y
   *     di N», so a planner without filters knows how long the list is and where they are in it;
   *   * the sort is the only choice, so it shows: the sorted column is the table's ONE
   *     emphasised column (brief part 1 §1 d) with «↓/↑» in its header; a click on the sorted
   *     header inverts it, a click on another sorts by that one; the sort is the page's `?sort=`
   *     (the grid calls `onSortChange`, the page writes it);
   *   * every row is 40px in every density — virtualisation needs one height (the text still
   *     follows the density).
   */
  readonly listing?: GridListing;
  /**
   * A click anywhere on a row — Transfers opens the row's «Perché» with `?row=`. The label is
   * then a button with the same effect, so the row is reachable by keyboard (ADR 0089). Pass a
   * stable callback (`useCallback`): it reaches every memoised row, and a new one per render
   * re-renders the whole window (see `columnLayoutEqual`).
   */
  readonly onRowClick?: (rowId: string) => void;
  /**
   * Set when the rows given are the top of a longer ranked list, so the grid's own download says
   * so — «le righe mostrate (50)», beside the list's «tutte le righe (23.352)» (audit A7, 2026-10-10).
   */
  readonly downloadScope?: 'shown';
}

export interface GridListing {
  /** The count's noun, pluralised: «{count, plural, one {# trasferimento} other {# trasferimenti}}». */
  readonly countKey: string;
  /** The sort, from the page's `?sort=`; `null` keeps the rows' own order. The label column is `label`. */
  readonly sort: GridSort | null;
  readonly onSortChange: (sort: GridSort) => void;
}

/** The label column's key in a `GridSort` — `?sort=label:asc`. */
export const GRID_LABEL_SORT_KEY = 'label';
const LABEL_COLUMN_ID = '__label';

/**
 * The sort a header click asks for: the sorted column inverts; another column sorts
 * descending — a measure is read largest first — and the label ascending, A to Z.
 */
export function nextGridSort(current: GridSort | null, column: string): GridSort {
  if (current !== null && current.column === column) {
    return { column, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  }
  return { column, direction: column === GRID_LABEL_SORT_KEY ? 'asc' : 'desc' };
}

/** The rows in view, 1-based, from the virtualiser's visible range. */
export function gridPosition(range: { readonly startIndex: number; readonly endIndex: number } | null, total: number): {
  readonly from: number; readonly to: number;
} {
  if (total === 0) return { from: 0, to: 0 };
  if (range === null) return { from: 1, to: total };
  return { from: Math.min(total, range.startIndex + 1), to: Math.min(total, range.endIndex + 1) };
}

const sortKeyOf = (columnId: string): string => (columnId === LABEL_COLUMN_ID ? GRID_LABEL_SORT_KEY : columnId);
const columnIdOf = (sortKey: string): string => (sortKey === GRID_LABEL_SORT_KEY ? LABEL_COLUMN_ID : sortKey);

/** The emphasised header: `surface-emphasis-head` with a 3px ink rule on top (brief part 1 §1 d). */
const EMPHASIS_HEAD_STYLE: React.CSSProperties = { boxShadow: 'inset 0 3px 0 0 var(--bata-color-ink-primary)' };

/**
 * Column widths, shared between the TanStack `ColumnDef`s and the render-time column layout.
 * One constant rather than two, so the sticky offsets of the pinned columns cannot disagree
 * with the widths the header actually renders at.
 */
const LABEL_COLUMN_WIDTH = 280;
/**
 * 128, not 112. `table-layout: fixed` means a value too wide for its column does not widen it —
 * it spills out of the cell, visibly, over the neighbour, because these cells set no `overflow`.
 * A formatted money measure is the case that did it: `COP 2,318,400` needs 117 px of content box
 * on /foundations/versions and had 111, so the figure and the column beside it overlapped.
 *
 * This is why the §12.13 check looks for elements whose `scrollWidth` exceeds their `clientWidth`
 * as well as for a scrolling page. The page did NOT scroll here — `documentElement.scrollWidth`
 * was within its budget throughout — so a check written only against page scroll would have
 * called an overlapping figure compliant.
 *
 * A money column genuinely needs more room than a units column, and sizing per measure `kind`
 * would be the better answer; this is the smaller one, and it buys ~17 px of headroom over the
 * widest value in the fixtures rather than exactly clearing it.
 */
const MEASURE_COLUMN_WIDTH = 128;

/**
 * The table features this grid registers, and nothing else — TanStack Table v9.
 *
 * **`docs/adr/0097` is where the decision is recorded** — the v8→v9 migration, and, one by one,
 * why each of the eleven unregistered features is a REFUSAL rather than a gap. Read it before
 * adding to this object: filtering, grouping, pagination, column visibility and row selection are
 * each absent for a stated reason, and four of them would let a screen state a number the platform
 * did not compute. (This comment carried that reasoning in full until the register had room for
 * it; ADR 0081 extended the register and ADR 0097 now carries it.)
 *
 * v8 bundled all sixteen stock features into every table and exposed their APIs whether or not
 * a table used them. v9 exposes an API only when its feature is registered here, so this object
 * is the exhaustive list of what the planning grid asks the table library to do:
 *
 *   `rowSortingFeature`     `enableSorting`, `getIsSorted`, `getToggleSortingHandler`
 *   `rowExpandingFeature`   `getIsExpanded` and the `expanded` state slice
 *   `columnSizingFeature`   `columnDef.size`, `header.getSize()`, `table.getTotalSize()`
 *
 * and no filtering, pagination, grouping, aggregation, faceting, pinning, visibility, ordering,
 * resizing or row selection — each of which this component renders itself or does not have.
 *
 * WHY THE SORT FUNCTIONS ARE REGISTERED BY NAME. In v9 a column's default `sortFn: 'auto'`
 * resolves against THIS registry rather than against a built-in table: sample the first rows,
 * pick a name, look it up, and silently fall back to `basic` when the name is not registered.
 * The entity column holds `Article 000123 – EU size run`, which auto-selects `alphanumeric`;
 * leaving the registry empty would have sorted it by raw string comparison instead, so
 * `Article 10` would sort before `Article 9` and nothing would have failed. `text` is the
 * fallback v9 reaches for when `alphanumeric` is absent, and is registered for the same reason.
 *
 * `basic` is registered too, and no column is left on `'auto'` — ADR 0091. `'auto'` decides by
 * sampling the first ten rows of the CURRENT model, so which comparator a column sorts by is a
 * property of the data that happened to load, not of the column. A measure column whose first
 * ten rows are all absent, and a measure column whose third row carries a `text` cell, resolve
 * differently from the same column definition, and neither reports it. Naming the comparator on
 * every column removes the sampling from the answer entirely; `basic` has to be in the registry
 * for a column to be able to name it, because v9's hard-coded fallback is reachable by accident
 * and not by name.
 *
 * Defined at module level, not inside the component: `tableFeatures` is a static declaration and
 * rebuilding it per render would rebuild the table's feature set per render.
 */
const GRID_FEATURES = tableFeatures({
  columnSizingFeature,
  rowExpandingFeature,
  rowSortingFeature,
  expandedRowModel: createExpandedRowModel(),
  sortedRowModel: createSortedRowModel(),
  sortFns: { alphanumeric: sortFn_alphanumeric, basic: sortFn_basic, text: sortFn_text },
});

type GridFeatures = typeof GRID_FEATURES;
type GridColumnDef = ColumnDef<GridFeatures, GridRow>;

// ── cell rendering ───────────────────────────────────────────────────────────

interface CellProps {
  readonly value: CellValue;
  readonly measure: MeasureDefinition;
  readonly locale: string;
  readonly isTotal: boolean;
}

/**
 * What a cell shows. The two absent states are written IN WORDS (ADR 9061): `textKey` is the
 * catalogue key of the words, and `titleKey` the same words (they are short) as the tooltip. The
 * sort never reads this text — it reads `MeasureDefinition.sortValue`, so an absent value still
 * sorts as absent, not alphabetically by its word.
 */
function formatCell({ value, measure, locale }: CellProps): { text: string; textKey?: string; titleKey?: string } {
  switch (value.kind) {
    case 'number':
      return {
        text: measure.currency
          ? formatCurrency(value.value, measure.currency, locale)
          : formatNumber(value.value, locale),
      };
    case 'text':
      return { text: value.value };
    // reqs/00 D8 — three distinguishable states, and the distinction is visible.
    case 'counted-zero':
      return { text: formatNumber(0, locale) };
    case 'not-ranged':
      return { text: '', textKey: 'grid.missing.notRanged', titleKey: 'grid.missing.notRanged' };
    case 'not-reported':
      return { text: '', textKey: 'grid.missing.notReported', titleKey: 'grid.missing.notReported' };
    default: {
      const x: never = value;
      throw new Error(String(x));
    }
  }
}

// ── the grid ─────────────────────────────────────────────────────────────────

/**
 * One column, resolved once per layout change rather than once per cell per frame.
 *
 * `measures.find(...)` inside a cell is O(columns) per cell — 676 lookups every frame at
 * Compact density. Resolving the layout up front turns that into one pass when the column set
 * changes.
 */
interface ColumnLayout {
  readonly id: string;
  readonly size: number;
  /** Sticky offset on the inline-start axis, or undefined when the column is not pinned. */
  readonly pinnedOffset: number | undefined;
  readonly isLabel: boolean;
  readonly measure: MeasureDefinition | undefined;
}

interface GridRowViewProps {
  readonly row: GridRow;
  readonly rowIndex: number;
  readonly rowHeight: number;
  /** The density, for the row's text size (brief part 1 §1). */
  readonly density: DensityMode;
  readonly columns: readonly ColumnLayout[];
  readonly locale: string;
  readonly t: TFunction;
  readonly isExpanded: boolean;
  readonly onToggleExpand: ((rowId: string) => void) | undefined;
  /** The column being edited IN THIS ROW, or null. Passing the whole editing state would give
   *  every row a new prop on every keystroke and defeat the memo. */
  readonly editingColumn: string | null;
  readonly onBeginEdit: (rowId: string, columnKey: string) => void;
  readonly onCommitEdit: (row: GridRow, columnKey: string, raw: string) => void;
  readonly onCancelEdit: () => void;
  readonly onLineageRequest: PlanningGridProps['onLineageRequest'];
  readonly onRowActivate: PlanningGridProps['onRowActivate'];
  readonly onRowClick: PlanningGridProps['onRowClick'];
  /** The sorted column of a listing — the one emphasised column — or null. */
  readonly emphasisColumn: string | null;
}

/**
 * Two `ColumnLayout` arrays describe the same layout.
 *
 * WHY THIS EXISTS — a defect found by measuring `09.U2` on a real chapter screen rather than
 * on the benchmark harness (ADR 0018, `bata-reqs` ADR 0018).
 *
 * `columnLayout` is derived with `useMemo`, so on the harness — which passes a module-level
 * `measures` array and a module-level `pinnedColumns` — its identity is stable and every row's
 * memo holds. A chapter screen does not do that. `AllocationScreen` writes
 * `measures={[UNITS_MEASURE, COVER_MEASURE]}` inline, and no screen passes `pinnedColumns` at
 * all, which used to mean a fresh `[]` per render. Either one changes the memo's dependency
 * on every render, which rebuilds `columnLayout`, which hands every memoised row a new
 * `columns` prop, which re-renders every visible cell on every scroll frame. The memo was
 * still there, still looked correct in review, and did nothing — on exactly the screens the
 * exit criterion is about, and on none of the ones it was measured on.
 *
 * Comparing the layout by VALUE rather than by identity makes the memo hold whatever the
 * caller passes. Thirteen field comparisons per row are three orders of magnitude cheaper
 * than re-rendering thirteen cells, and a shared component cannot police how nine chapter
 * agents spell a prop.
 */
export function columnLayoutEqual(a: readonly ColumnLayout[], b: readonly ColumnLayout[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const x = a[i]!;
    const y = b[i]!;
    if (
      x.id !== y.id || x.size !== y.size || x.pinnedOffset !== y.pinnedOffset
      || x.isLabel !== y.isLabel || x.measure !== y.measure
    ) return false;
  }
  return true;
}

/**
 * Shallow prop equality with `columns` compared by value.
 *
 * Enumerating the keys rather than listing them by hand is deliberate: a comparator that
 * names its props falls silently out of date the day a prop is added, and the symptom is a
 * stale cell rather than a build failure.
 */
function gridRowPropsEqual(prev: GridRowViewProps, next: GridRowViewProps): boolean {
  const keys = Object.keys(prev) as (keyof GridRowViewProps)[];
  if (keys.length !== Object.keys(next).length) return false;
  return keys.every((key) => (key === 'columns'
    ? columnLayoutEqual(prev.columns, next.columns)
    : Object.is(prev[key], next[key])));
}

/**
 * THE MEMO IS THE PERFORMANCE DECISION, not an optimisation detail.
 *
 * Scrolling one frame at Compact density moves the window by a handful of rows, but React
 * re-renders every row in it — 52 rows × 13 columns = 676 cells, each carrying a formatted
 * number, an ICU-translated lineage label and an icon. Measured, that was 69 ms per frame, and
 * the measurement was flat at 1,000, 10,000 and 100,000 rows: a cost independent of the model
 * size, which is what proves it is the VISIBLE WINDOW being re-rendered rather than
 * virtualisation failing.
 *
 * With the row memoised on its identity, a frame re-renders only the rows that actually entered
 * the window. The rest are skipped, and the frame cost becomes proportional to the scroll delta
 * rather than to the viewport height — which is what makes 60 fps reachable at all.
 */
const GridRowView = memo(function GridRowView({
  row, rowIndex, rowHeight, density, columns, locale, t, isExpanded, onToggleExpand,
  editingColumn, onBeginEdit, onCommitEdit, onCancelEdit, onLineageRequest, onRowActivate, onRowClick, emphasisColumn,
}: GridRowViewProps): React.JSX.Element {
  const activate = onRowActivate ?? onRowClick;
  if (row.group === true) {
    // A group's heading: its name and count across the row, like the approvals queue's (ADR 9038).
    return (
      <tr
        aria-rowindex={rowIndex}
        data-row-kind="group"
        style={{ height: GRID_GROUP_ROW_HEIGHT }}
        className="bg-surface-constants border-hairline border-b"
      >
        <th scope="colgroup" colSpan={columns.length} className="text-ink-primary px-2 text-start text-[12px] font-bold">
          {row.label}
        </th>
      </tr>
    );
  }
  return (
    <tr
      aria-rowindex={rowIndex}
      // A totals row is 44px in every density (brief part 1 §1); every other row takes the
      // density's height and text size.
      style={densityRowStyle(density, row.isTotal === true ? DENSITY_TOTAL_ROW_HEIGHT : rowHeight)}
      // …and sits under a 2px ink rule with its figures at 700 (brief part 1 §1, pkgC2).
      className={[
        row.isTotal === true ? 'border-ink-primary border-t-2 font-bold' : 'border-hairline border-b',
        onRowClick === undefined ? '' : 'cursor-pointer',
      ].join(' ')}
      {...(row.isTotal === true ? { 'data-row-kind': 'total' } : {})}
      {...(onRowClick === undefined ? {} : {
        onClick: (event: React.MouseEvent<HTMLTableRowElement>) => {
          // A control inside the row (the expander, the lineage button, an editor) acts for itself.
          if ((event.target as HTMLElement).closest('button, input, a') !== null) return;
          onRowClick(row.id);
        },
      })}
    >
      {columns.map((column) => {
        const { isLabel, measure, pinnedOffset } = column;
        const isPinned = pinnedOffset !== undefined;
        const value = row.values[column.id];
        const lineage = row.lineage[column.id];
        const isEditing = editingColumn === column.id;
        const emphasised = emphasisColumn === column.id;

        return (
          <td
            key={column.id}
            className={[
              // §6 — radius 0 on grid cells themselves; sharp cells read as tabular.
              'border-hairline rounded-cell border-e px-2',
              // A totals row's label keeps its case here: in the grid `isTotal` also marks a GROUP's
              // row (the markdown batch's groups), whose label is a name, not «Totale».
              isLabel ? 'text-body text-ink-primary font-ui text-start' : 'text-end',
              // §4.3 / §12.5 — every number in a grid is IBM Plex Mono and aligns vertically
              // within its column. `tabular-nums` matters only while the font is still loading,
              // which is exactly when a column jumping out of alignment would cause a misread.
              !isLabel ? 'font-numeric tabular-nums' : '',
              !isLabel && row.isTotal === true
                ? 'text-numeric font-bold'
                : !isLabel ? 'text-numeric' : '',
              emphasised
                ? `bg-surface-emphasis ${row.isTotal === true ? '' : 'font-semibold'}`
                : isPinned ? 'bg-surface-raised' : '',
              isPinned ? 'z-grid-pinned' : '',
              FOCUS_RING_INSET,
            ].join(' ')}
            style={{
              width: column.size,
              ...(isPinned ? { position: 'sticky', insetInlineStart: pinnedOffset } : {}),
              ...(isLabel ? { paddingInlineStart: 8 + row.depth * 16 } : {}),
            }}
            tabIndex={-1}
            onDoubleClick={() => onBeginEdit(row.id, column.id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === 'F2') onBeginEdit(row.id, column.id);
            }}
          >
            {isLabel
              ? (
                /**
                 * ONE LINE (client rule 2, ADR 9028). The row's height is the density's, and a
                 * label that wrapped — a transfer's `article · size · donor · recipient` in the
                 * fixed label column — spilled onto a second line (issue 215). The label is cut
                 * with an ellipsis at the column's edge and said whole in its tooltip and its
                 * accessible name; it is an identifier, not a reason, so ADR 9028's refusal to
                 * truncate a reason does not apply.
                 */
                <span className="flex min-w-0 items-center gap-1">
                  {row.hasChildren && onToggleExpand && (
                    <button
                      type="button"
                      onClick={() => onToggleExpand(row.id)}
                      aria-expanded={isExpanded}
                      aria-label={t(isExpanded ? 'grid.collapse' : 'grid.expand', { entity: row.label })}
                      className={`shrink-0 ${FOCUS_RING_INSET}`}
                    >
                      {isExpanded
                        ? <ChevronDown size={12} aria-hidden />
                        : <ChevronRight size={12} aria-hidden />}
                    </button>
                  )}
                  {activate
                    ? (
                      /**
                       * ADR 0089 — the row is activatable, so it says so to a screen reader
                       * and can be reached by keyboard. `stopPropagation` on the two keys the
                       * CELL treats as "begin edit": Enter already fires this button's click,
                       * and letting it bubble would open an editor on the label column as well.
                       */
                      <button
                        type="button"
                        onClick={(event) => { event.stopPropagation(); activate(row.id); }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === 'F2') event.stopPropagation();
                        }}
                        aria-label={t('grid.activateRow', { entity: row.label })}
                        title={row.label}
                        className={`min-w-0 truncate text-start underline-offset-2 hover:underline ${FOCUS_RING_INSET}`}
                      >
                        {row.label}
                      </button>
                    )
                    // One line: a long label is cut with an ellipsis, the whole of it in a
                    // tooltip that opens on focus too (brief part 1 §1, `TruncatedText`).
                    : <TruncatedText text={row.label} className="min-w-0" />}
                </span>
              )
              : isEditing && measure
                ? (
                  <input
                    autoFocus
                    defaultValue={value !== undefined && isNumeric(value) ? String(value.value) : ''}
                    onBlur={(e) => onCommitEdit(row, column.id, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') onCommitEdit(row, column.id, e.currentTarget.value);
                      if (e.key === 'Escape') onCancelEdit();
                    }}
                    className="font-numeric text-numeric border-hairline bg-surface-raised text-ink-primary w-full border text-end tabular-nums"
                    aria-label={t('grid.editCell', { measure: t(measure.labelKey), entity: row.label })}
                  />
                )
                : (
                  <span className="inline-flex items-center justify-end gap-1">
                    {(() => {
                      if (value === undefined || !measure) return null;
                      const { text, textKey, titleKey } = formatCell({
                        value, measure, locale, isTotal: row.isTotal === true,
                      });
                      return titleKey
                        // ADR 0092 — `ink-secondary`. `not-ranged` and `not-reported` are
                        // STATED, not hidden (docs/adr/0014); a mark at 2.17:1 states them the
                        // way a watermark does.
                        ? <abbr title={t(titleKey)} className="text-ink-secondary whitespace-nowrap no-underline">{textKey === undefined ? text : t(textKey)}</abbr>
                        : <span>{text}</span>;
                    })()}
                    {lineage && onLineageRequest && (
                      // G2.2 — every number answers "where did this come from", in place,
                      // without leaving the screen.
                      <button
                        type="button"
                        onClick={() => onLineageRequest(row.id, column.id, lineage)}
                        aria-label={t('grid.lineage', { measure: lineage.measure, entity: row.label })}
                        // ADR 0092 — a control's icon is non-text content and owes §12.7's
                        // 3:1. `ink-muted` is 2.40:1 in light against the raised surface.
                        className={`text-ink-secondary hover:text-ink-primary ${FOCUS_RING_INSET}`}
                      >
                        <Info size={10} aria-hidden />
                      </button>
                    )}
                  </span>
                )}
          </td>
        );
      })}
    </tr>
  );
}, gridRowPropsEqual);

/** The default for `pinnedColumns`. A `[]` literal in the parameter list is a NEW array on
 *  every render, which is one of the two ways the row memo was being defeated — see
 *  `columnLayoutEqual`. Every screen in the product relies on this default. */
const NO_PINNED_COLUMNS: readonly string[] = [];

export function PlanningGrid({
  rows, measures, density, pinnedColumns = NO_PINNED_COLUMNS, onEdit, onLineageRequest,
  onRowActivate, labelKey, viewportHeight, overscan = 8, listing, onRowClick, downloadScope,
}: PlanningGridProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  // A listing's rows are 40px in every density (brief part 2 §7.1): virtualisation needs one height.
  const rowHeight = listing === undefined ? densityRowHeight[density] : densityRowHeight.default;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [ownSorting, setSorting] = useState<SortingState>([]);
  const listingSort = listing?.sort ?? null;
  const sorting = useMemo<SortingState>(() => (listing === undefined
    ? ownSorting
    : listingSort === null ? [] : [{ id: columnIdOf(listingSort.column), desc: listingSort.direction === 'desc' }]),
  [listing, listingSort, ownSorting]);
  const emphasisColumn = listingSort === null ? null : columnIdOf(listingSort.column);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  // A group's heading row opens on its rows (ADR 9038); a toggle the planner made still wins.
  const tableExpanded = useMemo<ExpandedState>(() => {
    const open: Record<string, boolean> = {};
    for (const row of rows) if (row.group === true) open[row.id] = true;
    return { ...open, ...(typeof expanded === 'boolean' ? {} : expanded) };
  }, [rows, expanded]);
  const [editing, setEditing] = useState<{ rowId: string; columnKey: string } | null>(null);

  const columns = useMemo<GridColumnDef[]>(() => {
    const labelColumn: GridColumnDef = {
      id: '__label',
      accessorFn: (row) => row.label,
      header: () => t('grid.column.entity'),
      size: LABEL_COLUMN_WIDTH,
      enableSorting: true,
      // Named, not sampled — ADR 0091. The entity column is reference data and
      // `Article 10` must sort after `Article 9`.
      sortFn: 'alphanumeric',
    };
    const measureColumns = measures.map<GridColumnDef>((measure) => ({
      id: measure.key,
      /**
       * The SORT value of a measure cell — ADR 0091, and reqs/00 D8's four distinguishable
       * kinds of nothing carried through the comparator rather than dropped at it.
       *
       * This used to be `isNumeric(v) ? v.value : null`, which collapsed all four non-`number`
       * kinds onto one value, and `null` compares as less than every real number. A
       * `counted-zero` — a measured zero, rendered in the cell as `0` — therefore sorted with
       * the absences instead of with the other zeros: two cells reading `0` on screen landed at
       * opposite ends of an ascending sort, and nothing failed. `docs/adr/0014` is the same rule
       * seen from the other side: a gap is not a zero, and a counted zero is not a gap.
       *
       *   `number`         its value
       *   `counted-zero`   0 — measured, and it sorts where it reads
       *   `text`           the string; a present value sorts among present values
       *   `not-ranged` / `not-reported` / absent   `undefined`, and `sortUndefined: 'last'`
       *
       * `'last'` rather than a low value, and in BOTH directions: v9 returns on `sortUndefined`
       * before it applies `desc`, so reversing the sort does not promote the gaps to the top of
       * the screen. An absence is not the smallest measurement — it is not a measurement.
       *
       * A column that declares `sortValue` orders by it instead (`docs/adr/6000`, amendment
       * 2026-09-30 (c)): a day shown as a formatted string sorted by that string put «Oct 1» before
       * «Sep 28». Its `null` is mapped to `undefined`, so it sorts last in both directions like
       * every other absence.
       */
      accessorFn: (row) => {
        if (measure.sortValue !== undefined) return measure.sortValue(row) ?? undefined;
        const v = row.values[measure.key];
        if (v === undefined) return undefined;
        if (v.kind === 'text') return v.value;
        return toNumberOrNull(v) ?? undefined;
      },
      sortFn: 'basic',
      sortUndefined: 'last',
      header: () => (
        <span className="inline-flex flex-col items-end">
          <span>{t(measure.labelKey)}</span>
          {/* §4.3 — the unit of measure sits beside the column header rather than
              being repeated in every cell. */}
          <span className="text-caption text-ink-secondary font-normal">{t(measure.unitKey)}</span>
        </span>
      ),
      size: MEASURE_COLUMN_WIDTH,
      enableSorting: true,
    }));
    return [labelColumn, ...measureColumns];
  }, [measures, t]);

  /**
   * G2.3: "Server-side sort, filter and pagination BEYOND the resident window — never 'load
   * everything and hope'." Client sorting applies to the resident window only; the caller
   * re-queries when the window is not the whole set.
   *
   * The row models are registered in `GRID_FEATURES` above rather than passed here: v9 moved
   * `getSortedRowModel: getSortedRowModel()` and its siblings out of the table options and into
   * feature slots, and made the core model automatic. v8's `manualPagination: true` is gone with
   * them — with no `rowPaginationFeature` registered there is no pagination stage in the
   * pipeline to turn off, which is the stronger version of the same statement.
   */
  const table = useTable({
    features: GRID_FEATURES,
    data: rows,
    columns,
    state: { sorting, expanded: tableExpanded },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    getRowId: (row) => row.id,
    getSubRows: (row) => row.children,
  });

  const tableRows = table.getRowModel().rows;
  const dataRowCount = tableRows.filter((row) => row.original.group !== true).length;

  const virtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => {
      const row = tableRows[index]?.original;
      if (row?.group === true) return GRID_GROUP_ROW_HEIGHT;
      return row?.isTotal === true ? DENSITY_TOTAL_ROW_HEIGHT : rowHeight;
    },
    overscan,
    /**
     * The viewport rect BEFORE the ResizeObserver has fired.
     *
     * Without it the virtualiser measures a height of zero on the first pass and renders no
     * rows at all — which is invisible in a browser, because the observer fires a frame
     * later, and fatal anywhere there is no layout engine: a server-rendered first paint,
     * and every unit test. The caller has already told us the height, so there is no reason
     * to wait to be told again.
     */
    initialRect: { width: 1440, height: viewportHeight },
  });

  const virtualRows = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = virtualRows[0]?.start ?? 0;
  const paddingBottom = totalSize - (virtualRows[virtualRows.length - 1]?.end ?? 0);

  /**
   * The full column layout, resolved once per layout change. Pinned columns are sticky at the
   * inline start with `insetInlineStart`, never `left` (G6) — the grid must not need rewriting
   * when Urdu or Hebrew arrives.
   */
  /**
   * NOTE THE DEPENDENCIES: `measures` and `pinnedColumns`, and deliberately NOT `table`.
   *
   * `useTable` returns a table object whose identity is not stable across renders. Deriving the
   * layout from it gives this memo a changed dependency every frame, which gives every row a
   * changed `columns`
   * prop, which makes `GridRowView`'s memo miss on every row of every frame — the memo is still
   * there, still looks correct in review, and does nothing. Measured, that was the difference
   * between 75 ms and 3 ms per frame.
   *
   * The column sizes are therefore constants shared with the `ColumnDef`s above rather than read
   * back off the table.
   */
  const columnLayout = useMemo<ColumnLayout[]>(() => {
    const pinnedIds = ['__label', ...pinnedColumns];
    const sizeOf = (id: string) => (id === '__label' ? LABEL_COLUMN_WIDTH : MEASURE_COLUMN_WIDTH);

    const offsets = new Map<string, number>();
    let cursor = 0;
    for (const id of pinnedIds) {
      if (id !== '__label' && !measures.some((m) => m.key === id)) continue;
      offsets.set(id, cursor);
      cursor += sizeOf(id);
    }

    return [
      { id: '__label', size: LABEL_COLUMN_WIDTH, pinnedOffset: offsets.get('__label'), isLabel: true, measure: undefined },
      ...measures.map((measure) => ({
        id: measure.key,
        size: MEASURE_COLUMN_WIDTH,
        pinnedOffset: offsets.get(measure.key),
        isLabel: false,
        measure,
      })),
    ];
  }, [pinnedColumns, measures]);

  const pinnedOffsets = useMemo(
    () => new Map(
      columnLayout
        .filter((c): c is ColumnLayout & { pinnedOffset: number } => c.pinnedOffset !== undefined)
        .map((c) => [c.id, c.pinnedOffset]),
    ),
    [columnLayout],
  );

  /**
   * Stable callbacks. A new function identity on any of these would give every memoised row a
   * changed prop on every frame and defeat the memo entirely — which is the usual way a
   * memoised list quietly stops being memoised.
   */
  const toggleExpanded = useCallback((rowId: string) => {
    setExpanded((current) => {
      const state = typeof current === 'boolean' ? {} : current;
      return { ...state, [rowId]: !state[rowId] };
    });
  }, []);

  const cancelEdit = useCallback(() => setEditing(null), []);

  const beginEdit = useCallback((rowId: string, columnKey: string) => {
    const measure = measures.find((m) => m.key === columnKey);
    if (!measure?.editable) return;
    setEditing({ rowId, columnKey });
  }, [measures]);

  const commitEdit = useCallback((row: GridRow, columnKey: string, raw: string) => {
    setEditing(null);
    if (!onEdit) return;
    const previous = row.values[columnKey] ?? { kind: 'not-reported' as const };
    const parsed = Number(raw.replace(/[^\d.,-]/g, '').replace(',', '.'));
    if (!Number.isFinite(parsed)) return;
    onEdit({ rowId: row.id, columnKey, previous, next: { kind: 'number', value: parsed } });
  }, [onEdit]);

  return (
    <div className="flex flex-col">
      {/**
        * No density control here any more — ADR 0088 as amended, audit v5b D-08. It sat
        * above every grid and read as a setting of that grid; it is a planner's preference and
        * lives in the identity menu, named for what it governs ("in planning grids"). The row
        * height is still applied HERE and nowhere else.
        */}
      {/*
        The grid's CSV — ADR 9010: every table offers it. Every row, collapsed children
        included, in the order the grid is sorted into (`grid-export.ts`). Nothing is cut:
        the grid is windowed, not capped (ADR 0097). Above the grid, at the inline end (F7):
        reached before the rows, not after a viewport of them.
      */}
      {listing !== undefined && (
        <ListingBand
          listing={listing}
          total={dataRowCount}
          position={listingPosition(virtualizer.range, tableRows.map((row) => row.original))}
          sortedLabel={listingSort === null ? null : sortedLabel(listingSort.column, measures, t)}
        />
      )}
      <div className="mb-2">
        <RowCapNotice
          shown={dataRowCount}
          total={dataRowCount}
          always
          {...(downloadScope === undefined ? {} : { scope: downloadScope })}
          exportRequest={() => gridRequest({
            id: labelKey,
            titleKey: labelKey,
            // A group's heading row is not a row of data (ADR 9038).
            rows: table.getSortedRowModel().flatRows.map((row) => row.original).filter((row) => row.group !== true),
            measures,
            t,
          })}
        />
      </div>
      <div
      // §12.8 — full keyboard operability including the grid. The viewport is
      // focusable so a keyboard user reaches the rows without tabbing through
      // 100,000 cells; arrow keys then move the roving tabindex.
      ref={scrollRef}
      tabIndex={0}
      role="region"
      aria-label={t(labelKey)}
      /**
       * Marks THE PLANNING GRID, as opposed to the many ordinary `<table>`s a chapter screen
       * renders inside its panels. The row heights — Comfortable 48, Normal 40, Compact 32,
       * brief part 1 §1 re-stepping design/09 §6's 40/32/24 — are this component's contract
       * (and `DataTable`'s where it opts in with `density`): `densityRowHeight[density]`. `e2e/compact-density.spec.ts` needs to be able to say which
       * rows it is entitled to measure, and `tbody tr` cannot say it.
       */
      data-planning-grid=""
      /*
       * The box fits what it holds, up to the viewport it was given.
       *
       * `height: viewportHeight` drew a 418px bordered box around three rows — audit v4
       * G-22, "a riquadro vuoto under a table reads as rows that failed to load". The height
       * is now a CEILING: a short grid ends where its rows end, a long one stops at
       * `viewportHeight` and scrolls, which is the only case the virtualiser needs a
       * bounded box for. The same for width: `w-fit max-w-full` hugs a three-column grid
       * instead of drawing its border across the whole panel, and a wide one still takes
       * the full width §6 gives it, and scrolls past it.
       *
       * `overflow-x-auto` beside `overflow-auto` is not redundant: it is the hook the base
       * stylesheet's scroll shadow keys on, so a grid wider than its column shows that it is
       * (G-27 — on a tablet two of the three measures sat past the edge with no sign of them).
       */
      className={`border-hairline bg-surface-raised relative w-fit max-w-full overflow-auto overflow-x-auto border ${FOCUS_RING_WITHIN}`}
      style={{ maxHeight: viewportHeight }}
    >
      <table
        className="border-collapse"
        style={{
          width: table.getTotalSize(),
          /**
           * `table-layout: fixed` is a PERFORMANCE REQUIREMENT here, not a styling preference.
           *
           * With auto layout the browser must measure the content of every cell to resolve the
           * column widths, and it redoes that whenever the DOM changes — which is every scroll
           * frame. Measured on 100k rows at Compact density, auto layout cost ~32 ms per frame
           * while React was only re-rendering ten rows: the work was all in style and layout.
           *
           * Every column here already declares an explicit width, and the pinned columns'
           * sticky offsets are computed FROM those widths, so fixed layout is also the more
           * correct choice: it guarantees the rendered width is the width the offsets assumed.
           */
          tableLayout: 'fixed',
        }}
        aria-rowcount={tableRows.length}
      >
        <thead
          // Frozen headers. `position: sticky` on the header cells rather than the row,
          // because a sticky <tr> is not honoured consistently.
          className="bg-surface-sunken"
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const offset = pinnedOffsets.get(header.column.id);
                const isPinned = offset !== undefined;
                const emphasised = emphasisColumn === header.column.id;
                const arrow = emphasised && listingSort !== null ? (listingSort.direction === 'desc' ? '↓' : '↑') : null;
                return (
                  <th
                    key={header.id}
                    scope="col"
                    className={[
                      emphasised
                        ? 'bg-surface-emphasis-head text-ink-primary font-semibold'
                        : 'bg-surface-sunken text-ink-secondary',
                      'border-hairline text-label sticky top-0 border-b px-2 py-1',
                      header.column.id === '__label' ? 'text-start' : 'text-end',
                      isPinned ? 'z-grid-pinned-header' : 'z-grid-header',
                      FOCUS_RING_INSET,
                    ].join(' ')}
                    style={{
                      width: header.getSize(),
                      ...(isPinned ? { position: 'sticky', insetInlineStart: offset } : {}),
                      ...(emphasised ? EMPHASIS_HEAD_STYLE : {}),
                    }}
                    aria-sort={
                      header.column.getIsSorted() === 'asc' ? 'ascending'
                        : header.column.getIsSorted() === 'desc' ? 'descending' : 'none'
                    }
                  >
                    <button
                      type="button"
                      onClick={listing === undefined
                        ? header.column.getToggleSortingHandler()
                        : () => listing.onSortChange(nextGridSort(listingSort, sortKeyOf(header.column.id)))}
                      className={`w-full text-[inherit] ${header.column.id === '__label' ? 'text-start' : 'text-end'}`}
                    >
                      <span className="inline-flex items-start gap-1">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {arrow !== null && <span aria-hidden data-sort-arrow="">{arrow}</span>}
                      </span>
                    </button>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {paddingTop > 0 && <tr style={{ height: paddingTop }} aria-hidden />}
          {virtualRows.map((virtualRow) => {
            const tableRow = tableRows[virtualRow.index];
            if (!tableRow) return null;
            return (
              <GridRowView
                key={tableRow.id}
                row={tableRow.original}
                rowIndex={virtualRow.index + 1}
                rowHeight={rowHeight}
                density={density}
                columns={columnLayout}
                locale={i18n.language}
                t={t}
                isExpanded={tableRow.getIsExpanded()}
                onToggleExpand={toggleExpanded}
                // Written out rather than `editing?.rowId === tableRow.id ? editing.columnKey`:
                // v9 widened `row.id` enough that the optional chain no longer narrows `editing`
                // to non-null in the true branch, and an explicit null check says the same thing
                // without depending on how the library types one field.
                editingColumn={editing !== null && editing.rowId === tableRow.id ? editing.columnKey : null}
                onBeginEdit={beginEdit}
                onCommitEdit={commitEdit}
                onCancelEdit={cancelEdit}
                onLineageRequest={onLineageRequest}
                onRowActivate={onRowActivate}
                onRowClick={onRowClick}
                emphasisColumn={emphasisColumn}
              />
            );
          })}
          {paddingBottom > 0 && <tr style={{ height: paddingBottom }} aria-hidden />}
        </tbody>
        </table>
      </div>
    </div>
  );
}

// ── the listing band ─────────────────────────────────────────────────────────

/** The sorted column's name as the header says it. */
function sortedLabel(column: string, measures: readonly MeasureDefinition[], t: TFunction): string {
  if (column === GRID_LABEL_SORT_KEY) return t('grid.column.entity');
  const measure = measures.find((m) => m.key === column);
  return measure === undefined ? column : t(measure.labelKey);
}

/** «{N} trasferimenti · ordinati per {colonna} ↓ · righe X–Y di N», above the header (brief part 2 §7.1). */
function ListingBand({ listing, total, position, sortedLabel: label }: {
  readonly listing: GridListing;
  readonly total: number;
  readonly position: { readonly from: number; readonly to: number };
  readonly sortedLabel: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <p data-grid-listing="" className="text-ink-primary mb-2 flex flex-wrap items-baseline gap-2 text-[12px] leading-4">
      <strong className="font-numeric font-semibold">{t(listing.countKey, { count: total })}</strong>
      {label !== null && listing.sort !== null && (
        <>
          <span aria-hidden>·</span>
          <span>{t(listing.sort.direction === 'desc' ? 'grid.listing.sortedDesc' : 'grid.listing.sortedAsc', { column: label })}</span>
        </>
      )}
      <span aria-hidden>·</span>
      <span className="font-numeric">{t('grid.listing.position', { from: position.from, to: position.to, total })}</span>
    </p>
  );
}
